import React from 'react';
import { Helmet } from 'react-helmet-async';
import {
  SITE_NAME,
  getIslandCapabilities,
} from 'alakai-aloha-shared';
import ApexHomepage from '../components/apex/ApexHomepage';
import BrandExplainer from '../components/homepage/BrandExplainer';
import ItineraryWorkflow from '../components/homepage/ItineraryWorkflow';
import WeatherToolPreview from '../components/homepage/WeatherToolPreview';
import HomepagePlanningSelector from '../features/homepage-planning/HomepagePlanningSelector';
import { useLazyLoad } from '../hooks/useLazyLoad';
import { getRuntimeSiteContext } from '../lib/siteContext';

const RegionsGuidePreview = React.lazy(() => import('../components/homepage/RegionsGuidePreview'));
const BlogShowcase = React.lazy(() => import('../components/homepage/BlogShowcase'));
const BeachConditionsPreview = React.lazy(() => import('../components/homepage/BeachConditionsPreview'));
const PhotoPlannerPreview = React.lazy(() => import('../components/homepage/PhotoPlannerPreview'));
const RestaurantsLibraryPreview = React.lazy(() => import('../components/homepage/RestaurantsLibraryPreview'));
const ActivitiesLibraryPreview = React.lazy(() => import('../components/homepage/ActivitiesLibraryPreview'));
const LodgingLibraryPreview = React.lazy(() => import('../components/homepage/LodgingLibraryPreview'));
const FinalCTA = React.lazy(() => import('../components/homepage/FinalCTA'));

function ToolComingSoonPanel({ title, body }) {
  return (
    <div className="rounded-lg border border-white/10 bg-white/5 p-8 text-center">
      <h3 className="text-2xl font-semibold text-white">{title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-white/70">{body}</p>
    </div>
  );
}

function HomepageDivider() {
  return (
    <div className="max-w-6xl mx-auto pt-8 pb-8">
      <div className="border-t border-gray-600"></div>
    </div>
  );
}

function LazyHomepageSection({ shouldLoad, loadingLabel, pendingLabel, children }) {
  return shouldLoad ? (
    <React.Suspense fallback={<div className="p-8 text-center text-gray-600">{loadingLabel}</div>}>
      {children}
    </React.Suspense>
  ) : (
    <div className="p-8 text-center text-gray-600">{pendingLabel}</div>
  );
}

function IslandHomepageSections() {
  const { islandConfig, island } = React.useMemo(() => getRuntimeSiteContext(), []);
  const capabilities = React.useMemo(() => getIslandCapabilities(island), [island]);
  const { ref: mapRef, shouldLoad: shouldLoadFromScroll } = useLazyLoad({
    rootMargin: '2000px',
  });
  const [shouldLoadFromTime, setShouldLoadFromTime] = React.useState(false);
  const [mapLoaded, setMapLoaded] = React.useState(false);
  const [shouldLoadBeachConditions, setShouldLoadBeachConditions] = React.useState(false);
  const [shouldLoadBlog, setShouldLoadBlog] = React.useState(false);
  const [shouldLoadPhotoPlanner, setShouldLoadPhotoPlanner] = React.useState(false);
  const [shouldLoadRestaurants, setShouldLoadRestaurants] = React.useState(false);
  const [shouldLoadActivities, setShouldLoadActivities] = React.useState(false);
  const [shouldLoadLodging, setShouldLoadLodging] = React.useState(false);
  const [shouldLoadFinalCTA, setShouldLoadFinalCTA] = React.useState(false);

  React.useEffect(() => {
    const timer = setTimeout(() => setShouldLoadFromTime(true), 5000);
    return () => clearTimeout(timer);
  }, []);

  const shouldLoadMap = shouldLoadFromScroll || shouldLoadFromTime;

  React.useEffect(() => {
    if (!shouldLoadMap) {
      return undefined;
    }

    const failsafeTimer = setTimeout(() => {
      if (!mapLoaded) {
        console.log('Map loading timeout - starting chain load anyway');
        setShouldLoadBeachConditions(true);
      }
    }, 10000);

    return () => clearTimeout(failsafeTimer);
  }, [mapLoaded, shouldLoadMap]);

  React.useEffect(() => {
    if (!mapLoaded) {
      return undefined;
    }

    const timer = setTimeout(() => setShouldLoadBeachConditions(true), 500);
    return () => clearTimeout(timer);
  }, [mapLoaded]);

  React.useEffect(() => {
    if (!shouldLoadBeachConditions) {
      return undefined;
    }

    const timer = setTimeout(() => setShouldLoadBlog(true), 200);
    return () => clearTimeout(timer);
  }, [shouldLoadBeachConditions]);

  React.useEffect(() => {
    if (!shouldLoadBlog) {
      return undefined;
    }

    const timer = setTimeout(() => {
      if (capabilities.photoPlanner) {
        setShouldLoadPhotoPlanner(true);
      } else {
        setShouldLoadRestaurants(true);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [capabilities.photoPlanner, shouldLoadBlog]);

  React.useEffect(() => {
    if (!shouldLoadPhotoPlanner) {
      return undefined;
    }

    const timer = setTimeout(() => setShouldLoadRestaurants(true), 200);
    return () => clearTimeout(timer);
  }, [shouldLoadPhotoPlanner]);

  React.useEffect(() => {
    if (!shouldLoadRestaurants) {
      return undefined;
    }

    const timer = setTimeout(() => setShouldLoadActivities(true), 200);
    return () => clearTimeout(timer);
  }, [shouldLoadRestaurants]);

  React.useEffect(() => {
    if (!shouldLoadActivities) {
      return undefined;
    }

    const timer = setTimeout(() => setShouldLoadLodging(true), 200);
    return () => clearTimeout(timer);
  }, [shouldLoadActivities]);

  React.useEffect(() => {
    if (!shouldLoadLodging) {
      return undefined;
    }

    const timer = setTimeout(() => setShouldLoadFinalCTA(true), 200);
    return () => clearTimeout(timer);
  }, [shouldLoadLodging]);

  return (
    <>
      <div ref={mapRef}>
        {shouldLoadMap ? (
          <React.Suspense
            fallback={
              <div className="h-96 bg-gray-100 rounded-lg flex items-center justify-center">
                <div className="text-gray-600">Loading regions guide...</div>
              </div>
            }
          >
            <RegionsGuidePreview onLoad={() => setMapLoaded(true)} />
          </React.Suspense>
        ) : (
          <div className="h-96 bg-gray-100 rounded-lg flex items-center justify-center">
            <div className="text-gray-600">Scroll down to load regions guide</div>
          </div>
        )}
      </div>

      <HomepageDivider />

      <WeatherToolPreview />

      <HomepageDivider />

      {capabilities.beachConditions ? (
        shouldLoadBeachConditions ? (
          <React.Suspense fallback={<div className="p-8 text-center text-gray-600">Loading beach conditions preview...</div>}>
            <BeachConditionsPreview />
          </React.Suspense>
        ) : (
          <div className="p-8 text-center text-gray-600">Beach conditions preview will load soon...</div>
        )
      ) : (
        <ToolComingSoonPanel
          title={`${islandConfig.shortTitle} Beach Conditions Coming Soon`}
          body={`Daily beach-condition guidance is still being rolled out for ${islandConfig.title}. The island-specific edition will go live once local coverage and scoring are ready.`}
        />
      )}

      <HomepageDivider />

      <LazyHomepageSection
        shouldLoad={shouldLoadBlog}
        loadingLabel="Loading blog showcase..."
        pendingLabel="Blog showcase will load soon..."
      >
        <BlogShowcase />
      </LazyHomepageSection>

      <HomepageDivider />

      {capabilities.photoPlanner ? (
        <>
          {shouldLoadPhotoPlanner ? (
            <React.Suspense fallback={<div className="p-8 text-center text-gray-600">Loading photo planner preview...</div>}>
              <PhotoPlannerPreview />
            </React.Suspense>
          ) : (
            <div className="p-8 text-center text-gray-600">Photo planner preview will load soon...</div>
          )}

          <HomepageDivider />
        </>
      ) : null}

      <LazyHomepageSection
        shouldLoad={shouldLoadRestaurants}
        loadingLabel="Loading restaurants..."
        pendingLabel="Restaurants will load soon..."
      >
        <RestaurantsLibraryPreview />
      </LazyHomepageSection>

      <HomepageDivider />

      <LazyHomepageSection
        shouldLoad={shouldLoadActivities}
        loadingLabel="Loading activities..."
        pendingLabel="Activities will load soon..."
      >
        <ActivitiesLibraryPreview />
      </LazyHomepageSection>

      <HomepageDivider />

      <LazyHomepageSection
        shouldLoad={shouldLoadLodging}
        loadingLabel="Loading lodging..."
        pendingLabel="Lodging will load soon..."
      >
        <LodgingLibraryPreview />
      </LazyHomepageSection>

      <HomepageDivider />

      {shouldLoadFinalCTA ? (
        <React.Suspense fallback={<div className="p-8 text-center text-gray-600">Loading final call to action...</div>}>
          <FinalCTA />
        </React.Suspense>
      ) : (
        <div className="p-8 text-center text-gray-600">Final call to action will load soon...</div>
      )}
    </>
  );
}

function IslandHomepage({ islandConfig, siteUrl }) {
  return (
    <>
      <Helmet>
        <title>{`${islandConfig.title} Travel Guides, Itineraries & Tools | ${SITE_NAME}`}</title>
        <meta
          name="description"
          content={`Plan ${islandConfig.title} travel with in-depth guides, itinerary tools, and curated local profiles from ${SITE_NAME}.`}
        />
        <link rel="canonical" href={`${siteUrl}/`} />
      </Helmet>

      <div className="max-w-6xl mx-auto px-4">
        <HomepagePlanningSelector
          islandKey={islandConfig.key}
          islandName={islandConfig.shortTitle}
        />

        <HomepageDivider />

        <BrandExplainer />

        <HomepageDivider />

        <ItineraryWorkflow />

        <HomepageDivider />

        <IslandHomepageSections />
      </div>
    </>
  );
}

const Homepage = () => {
  const siteContext = React.useMemo(() => getRuntimeSiteContext(), []);

  if (siteContext.isApexHost) {
    return <ApexHomepage />;
  }

  return <IslandHomepage islandConfig={siteContext.islandConfig} siteUrl={siteContext.siteUrl} />;
};

export default Homepage;
